/**
 * Dessiner un code-barres à partir de SES données : une seule recette, pour
 * l'écran (BarcodeElement) et pour l'export (api/client.js, exportLabel).
 *
 * Avant le 2026-10-09, l'export ne dessinait rien : il recopiait le canvas caché
 * du calque AFFICHÉ (`barcode-canvas-<id>`). Un export par lot depuis un CSV
 * sortait donc, dans chacun de ses PDF, le code-barres de l'écran et non celui
 * de sa ligne (relevé par Jabir). Dessiné ici depuis `el.text`, il ne peut plus
 * diverger de la valeur exportée.
 */

// bwip-js pèse lourd et n'est nécessaire que si un document contient un
// code-barres : import dynamique partagé (même pattern que html2canvas
// dans EditorView) pour le sortir du bundle initial.
let _bwipjsPromise = null;
export const chargerBwipjs = () => {
    if (!_bwipjsPromise) _bwipjsPromise = import('bwip-js').then((m) => m.default || m);
    return _bwipjsPromise;
};

// Symbologies dont les modules sont CARRÉS : `height` les étirait (un QR code de
// 174 × 174 sortait en 174 × 116, modules rectangulaires, hors norme). bwip-js
// les dessine carrés quand on ne lui donne pas de hauteur — la clé doit alors
// être ABSENTE : `height: undefined` le fait lever (« not a realtype »).
export const SYMBOLOGIES_CARREES = new Set(['qrcode', 'datamatrix']);

/** Les options bwip-js d'un calque code-barres. */
export function optionsBwip(el) {
    return {
        bcid: el.symbology || 'ean13',
        text: el.text || '1234567890123',
        scale: el.bcScale || 3,
        ...(SYMBOLOGIES_CARREES.has(el.symbology) ? {} : { height: el.bcHeight || 15 }),
        includetext: el.showText !== false,
        textxalign: 'center',
        textsize: el.size || 8,
        textfont: el.font || 'Oswald',
        paddingwidth: el.bcMargin || 0,
        paddingheight: el.bcMargin || 0,
        inkspread: el.bcInkSpread || 0,
        barcolor: el.color ? el.color.replace('#', '') : '000000',
        backgroundcolor: el.bgColor && el.bgColor !== 'transparent' ? el.bgColor.replace('#', '') : undefined,
        textcolor: el.color ? el.color.replace('#', '') : '000000',
    };
}

/**
 * Le SVG de bwip-js ne porte qu'une `viewBox` : sans `width` ni `height`, une
 * <img> n'a pas de taille naturelle, et `object-contain` comme « Ajuster au
 * contenu » (fitFrame lit le canvas caché, que l'on peint à cette taille) ne
 * retrouveraient plus les proportions qu'ils avaient avec l'ancien bitmap.
 * Rend le même SVG, dimensionné en pixels à l'échelle `scale` de bwip-js.
 *
 * Sans `viewBox` lisible (une mise à jour de bwip-js qui changerait sa sortie),
 * elle LÈVE : l'appelant affiche alors l'échec sur le calque. Un code-barres
 * qui bougerait en silence, ou qui retomberait dans l'ancien bitmap flou,
 * partirait à l'impression sans que personne le voie.
 */
export function dimensionnerSvg(svg) {
    const ouvrante = svg.match(/<svg\b[^>]*>/);
    const boite = ouvrante && ouvrante[0].match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
    if (!boite) throw new Error('Code-barres non dessiné (SVG sans dimensions)');
    // Déjà dimensionné par bwip-js lui-même : rien à ajouter.
    if (/\swidth=/.test(ouvrante[0])) return svg;
    return svg.replace('<svg', `<svg width="${boite[1]}" height="${boite[2]}"`);
}

/** Le code-barres en SVG, sous forme d'URL data:. Lève si la valeur ne se code pas. */
export function svgDuCodeBarres(bwipjs, el) {
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(dimensionnerSvg(bwipjs.toSVG(optionsBwip(el))));
}

/**
 * Le message d'un refus de bwip-js, sans son préfixe technique :
 * « bwipp.ean13badLength#3006: EAN-13 must be 12 or 13 digits » → « EAN-13 must be 12 or 13 digits ».
 */
export function messageCodeBarres(e) {
    return String(e?.message || e || 'erreur de format').replace(/^bwipp?\.[\w.]+#?\d*:\s*/, '');
}

/** null si la valeur du calque se code, sinon la raison du refus. */
export async function refusCodeBarres(el) {
    const bwipjs = await chargerBwipjs();
    try {
        bwipjs.toSVG(optionsBwip(el));
        return null;
    } catch (e) {
        return messageCodeBarres(e);
    }
}

/**
 * L'image PNG d'un code-barres pour l'export : les mêmes pixels que le canvas
 * caché de BarcodeElement (le même SVG, peint à sa taille naturelle), mais
 * dessinée depuis les données du calque. LÈVE si la valeur ne se code pas :
 * un export ne part jamais avec un code-barres vide sans que personne le voie.
 */
export async function pngDuCodeBarres(el) {
    const bwipjs = await chargerBwipjs();
    const img = new Image();
    img.src = svgDuCodeBarres(bwipjs, el);
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
}
